import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Brain,
  Heart,
  Target,
  Lightbulb,
  Clock,
  Building2,
  Award,
  Truck,
  Users,
  ArrowRight,
} from 'lucide-react'
import { HeroSection } from '@/components/sections/HeroSection'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'
import { SectionTitle } from '@/components/ui/SectionTitle'
import { LocalizedLink } from '@/components/LocalizedLink'

type TimelineEvent = {
  year: string
  title: string
  description: string
  image: string
  imageAlt: string
}

const milestoneIcons = [Building2, Users, Award, Truck]

const values = [
  { key: 'rationality', Icon: Brain },
  { key: 'passion', Icon: Heart },
  { key: 'precision', Icon: Target },
  { key: 'curiosity', Icon: Lightbulb },
] as const

export default function History() {
  const { t, i18n } = useTranslation('common')

  const events = (t('history.timeline.events', { returnObjects: true }) as TimelineEvent[]) ?? []

  return (
    <>
      <Helmet>
        <title>{t('history.seo.title')}</title>
        <meta name="description" content={t('history.seo.description')} />
        <html lang={i18n.language} />
      </Helmet>

      <HeroSection
        imageSrc="/images/history-hero.jpg"
        title={t('history.hero.title')}
        slogan={t('history.hero.slogan')}
        primaryCta={{ label: t('buttons.contactUs'), to: 'contacts' }}
        secondaryCta={{ label: t('nav.services'), to: 'services' }}
      />

      {/* Intro + values */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <SectionTitle title={t('history.intro.heading')} />
              <div className="space-y-4 text-brand-text leading-relaxed">
                <p>{t('history.intro.body')}</p>
                <p>{t('history.founder.body')}</p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="absolute -inset-2 bg-brand-red/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <img
                src="/images/founder-office.jpg"
                alt={t('history.founder.imageAlt')}
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:-translate-y-1"
              />
            </motion.div>
          </div>

          <div className="mt-20">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-dark text-center mb-12">
              {t('about.values.heading')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="text-center group cursor-default"
                >
                  <div className="w-20 h-20 mx-auto mb-4 bg-brand-red rounded-full flex items-center justify-center shadow-md transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:bg-brand-dark">
                    <value.Icon className="w-9 h-9 text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                  </div>
                  <h4 className="font-semibold text-brand-dark mb-2">
                    {t(`about.values.${value.key}.title`)}
                  </h4>
                  <p className="text-sm text-brand-text/80 leading-relaxed">
                    {t(`about.values.${value.key}.description`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Timeline */}
      <Section className="bg-brand-dark">
        <Container>
          <SectionTitle
            title={t('history.timeline.heading')}
            subtitle={t('history.intro.heading')}
            centered
            light
          />

          <div className="relative mt-16">
            <motion.div
              className="hidden md:block absolute left-1/2 -translate-x-1/2 w-1 h-full bg-gradient-to-b from-brand-red/40 via-brand-red to-brand-red/40 rounded-full origin-top"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.6, ease: 'easeOut' }}
            />

            <div className="space-y-16">
              {events.map((event, index) => {
                const Icon = milestoneIcons[index] ?? Building2
                const isEven = index % 2 === 0

                return (
                  <motion.div
                    key={event.year}
                    initial={{ opacity: 0, x: isEven ? -60 : 60 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`relative flex flex-col md:flex-row items-center gap-8 ${
                      isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 w-full ${isEven ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                      <div
                        className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 overflow-hidden max-w-lg ${
                          isEven ? 'md:ml-auto' : 'md:mr-auto'
                        }`}
                      >
                        <div className="relative aspect-video overflow-hidden rounded-t-2xl">
                          <img
                            src={event.image}
                            alt={event.imageAlt}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <div className="p-6 text-left">
                          <span className="inline-flex items-center gap-2 text-brand-red font-bold text-2xl mb-2">
                            <Clock className="w-5 h-5" />
                            {event.year}
                          </span>
                          <h3 className="text-xl font-semibold text-brand-dark mb-2 group-hover:text-brand-red transition-colors">
                            {event.title}
                          </h3>
                          <p className="text-brand-text leading-relaxed">{event.description}</p>
                        </div>
                      </div>
                    </div>

                    <motion.div
                      className="hidden md:flex w-16 h-16 bg-brand-red rounded-full items-center justify-center z-10 shadow-xl shrink-0 border-4 border-white hover:scale-125 hover:rotate-12 transition-transform duration-300 cursor-default"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true, margin: '-100px' }}
                      transition={{ duration: 0.5, delay: index * 0.15, type: 'spring' }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </motion.div>

                    <div className="flex-1 hidden md:block" />
                  </motion.div>
                )
              })}
            </div>
          </div>
        </Container>
      </Section>

      {/* Closing quote + CTA */}
      <Section variant="light" className="!py-16">
        <Container className="text-center">
          <p className="text-brand-text text-lg md:text-xl max-w-3xl mx-auto leading-relaxed italic">
            «{t('history.closing.statement')}»
          </p>
          <p className="text-brand-dark font-bold mt-6">— {t('history.closing.signature')}</p>
        </Container>
      </Section>

      <section className="bg-brand-red py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('home.cta.heading')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">{t('home.cta.body')}</p>
          <LocalizedLink
            to="about"
            className="group inline-flex items-center gap-2 bg-white text-brand-red font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {t('nav.about')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </LocalizedLink>
        </div>
      </section>
    </>
  )
}
